import { useGetHeaderInfoQuery } from '@core/app/api/profileApi'
import useBroadcastChannel from '@core/utils/hooks/useBroadcastChannel'
import { usePageVisibility } from '@core/utils/hooks/usePageVisibility'
import type { SerializedError } from '@reduxjs/toolkit'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { useDispatch } from 'react-redux'
import ApplyDialogV2 from '@core/components/ApplyDialogV2'
import {
    applyJobV2Api,
    useDeleteApplyV2ProfileMutation,
    useGetAllQuestionsV2ApplyQuery,
    useGetApplyV2SettingsByIdQuery,
    useGetProfileV2ApplyQuery,
    useInitApplyV2Mutation,
    useLazyGetApplyV2SocketQuery,
    usePostApplyV2SendApplicationMutation,
    usePostValidateAllV2ApplyMutation,
    usePutApplyV2StepMutation,
} from '@core/app/api/applyJobV2Api'
import type {
    ApplyV2BaseResponse,
    ApplyV2Question,
    ApplyV2SettingsSection,
    ApplyV2SettingsSectionName,
    ApplyV2TempProfileData,
    CvParsingStatus,
    GetApplyV2ProfileResponse,
    GetApplyV2SettingsResponse,
    NavItemsState,
} from '@core/app/types/applyV2Type'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import type { PostData, PostInfo } from '@core/app/types/postsApiType'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import { createContext, useContext, useState, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useGetPostInfoQuery } from '@core/app/api/postsApi'
import useLocalStorageStore from '@core/utils/hooks/useLocalStorageStore'
import type { IApplyDialogV2LocalStorage } from '@core/app/types'
import ApplyV2ErrorBoundary from '@core/components/ApplyDialogV2/ApplyV2ErrorBoundary'

type ApplyV2ModalContextType = {
    isOpen: boolean
    isExpanded: boolean
    onResizeApply: () => void
    onOpenApply: (component?: ReactNode) => void
    onCloseApply: () => Promise<void>
    storageData: IApplyDialogV2LocalStorage | null
    content: ReactNode | null
    error: boolean
    settingsData: GetApplyV2SettingsResponse | null | undefined
    settingsError: FetchBaseQueryError | SerializedError | undefined | null
    isSettingsLoading: boolean
    settingsRefetch: () => void
    // isPostLoading: boolean
    postRefetch: () => void
    isPostFetching: boolean
    isSettingsFetching: boolean
    postData: PostData | null
    onSelectStep: (step: ApplyV2SettingsSectionName | null) => void | Promise<void>
    initData: GetApplyV2ProfileResponse | undefined | null
    initError: FetchBaseQueryError | SerializedError | undefined | null
    isInitError: boolean
    isInitLoading: boolean
    postProfileData: GetApplyV2ProfileResponse | undefined | null
    postProfileError: FetchBaseQueryError | SerializedError | undefined | null
    isPostProfileFetching: boolean
    isPostProfileSuccess: boolean
    postProfileRefetch: () => Promise<GetApplyV2ProfileResponse | null>
    isPostProfileLoading: boolean
    isRefetchingAfterCvParsing: boolean
    setIsRefetchingAfterCvParsing: (value: boolean) => void
    onNextStep: () => void
    navItems: (ApplyV2SettingsSection & { title: string; state: NavItemsState })[]
    setNavItemsStates: (items: { name: ApplyV2SettingsSection['name']; state: NavItemsState }[]) => void
    isFinalStep: boolean
    setIsFinalStep: (value: boolean) => void
    isPostApplied: boolean
    isProfileDataLoading: boolean
    existingSteps: ApplyV2SettingsSectionName[]
    isValidateLoading: boolean
    isValidateError: boolean
    postValidateAll: () => Promise<any>
    isPostApplicationExist: boolean
    isWebSocketConnected: boolean
    webSocketConnectionStatus: 'connecting' | 'open' | 'closed' | 'error'
    sendWebSocketMessage: (message: 'subscribe' | 'cv-parser') => boolean
    reconnectWebSocket: () => void
    initWebSocketConnection: () => Promise<void>
    cvParserStatus: 'file-search-started' | 'file-sent-to-parsing' | 'in-progress' | 'success' | null
    isApplicationDone: boolean
    setIsApplicationDoneHandler: (value: boolean) => void
    candidateId: number | null | undefined
    setAnonymityCheckbox: (value: boolean) => void
    questionsData: Array<ApplyV2Question>
    isQuestionsLoading: boolean
    isQuestionsFetching: boolean
    questionsRefetch: () => Promise<any>
    incrementProfileVisitCount: () => void
    applyKey: string
    isReviewFinalState: boolean
    setIsReviewFinalState: (value: boolean) => void
}

const ApplyV2ModalContext = createContext<ApplyV2ModalContextType>({
    isOpen: false,
    isExpanded: false,
    onOpenApply: () => {
        console.info(`ApplyV2ModalContext openModal() for ${window?.location?.href} is not provided`)
    },
    onCloseApply: () => {
        console.info(`ApplyV2ModalContext closeModal() for ${window?.location?.href} is not provided`)
        return Promise.resolve()
    },
    onResizeApply: () => {
        console.info(`ApplyV2ModalContext resizeModal() for ${window?.location?.href} is not provided`)
    },
    storageData: null,
    content: null,
    error: false,
    settingsData: null,
    settingsError: null,
    isSettingsLoading: false,
    settingsRefetch: () => {
        console.info(`ApplyV2ModalContext settingsRefetch() ${window?.location?.href} is not provided`)
    },
    // isPostLoading: false,
    postRefetch: () => {
        console.info(`ApplyV2ModalContext postRefetch() ${window?.location?.href} is not provided`)
    },
    isPostFetching: false,
    isSettingsFetching: false,
    postData: null,
    onSelectStep: (step: ApplyV2SettingsSectionName | null) => {
        console.info(`ApplyV2ModalContext onSelectStep() for ${window?.location?.href} is not provided`)
    },
    initData: null,
    postProfileData: null,
    postProfileError: null,
    isPostProfileFetching: false,
    isPostProfileSuccess: false,
    postProfileRefetch: () => {
        console.info(`ApplyV2ModalContext refetch() for ${window?.location?.href} is not provided`)
        return Promise.resolve(null)
    },
    initError: null,
    isInitError: false,
    isInitLoading: false,
    isPostProfileLoading: false,
    isRefetchingAfterCvParsing: false,
    setIsRefetchingAfterCvParsing: () => {
        console.info(
            `ApplyV2ModalContext setIsRefetchingAfterCvParsing() for ${window?.location?.href} is not provided`
        )
    },
    onNextStep: () => {},
    navItems: [],
    isFinalStep: false,
    setIsFinalStep: () => {
        console.info(`ApplyV2ModalContext setIsFinalStep() for ${window?.location?.href} is not provided`)
    },
    setNavItemsStates: () => {
        console.info(`ApplyV2ModalContext setNavItemsStates() for ${window?.location?.href} is not provided`)
    },
    isPostApplied: false,
    isProfileDataLoading: false,
    existingSteps: [],
    isValidateLoading: false,
    isValidateError: false,
    postValidateAll: () => {
        console.info(`ApplyV2ModalContext postValidateAll() for ${window?.location?.href} is not provided`)
        return Promise.resolve(null)
    },
    isPostApplicationExist: false,
    isWebSocketConnected: false,
    webSocketConnectionStatus: 'closed',
    sendWebSocketMessage: () => {
        console.info('sendWebSocketMessage() not implemented')
        return false
    },
    reconnectWebSocket: () => {
        console.info('reconnectWebSocket() not implemented')
    },
    initWebSocketConnection: async () => {
        console.info('initWebSocketConnection() not implemented')
    },
    cvParserStatus: null,
    isApplicationDone: false,
    setIsApplicationDoneHandler: (value: boolean) => {
        console.log('ddd')
    },
    candidateId: 0,
    setAnonymityCheckbox: () => {
        console.log('setAnonymityCheckbox')
    },
    questionsData: [],
    isQuestionsFetching: false,
    isQuestionsLoading: false,
    questionsRefetch: () => {
        return Promise.resolve([])
    },
    incrementProfileVisitCount: () => {
        console.info(`ApplyV2ModalContext incrementProfileVisitCount() for ${window?.location?.href} is not provided`)
    },
    applyKey: '',
    isReviewFinalState: false,
    setIsReviewFinalState: () => {
        console.info(`ApplyV2ModalContext setIsReviewFinalState() for ${window?.location?.href} is not provided`)
    },
})

export const ApplyV2ModalProvider = ({ children }: { children: ReactNode }) => {
    const { id } = useParams()
    const dispatch = useDispatch()
    const { getTitle } = useApplyV2NavTranslations()
    const location = useLocation()
    const [isOpen, setIsOpen] = useState(false)
    const [content, setContent] = useState<ReactNode | null>(null)
    const [isExpanded, setIsExpanded] = useState(false)
    const [error, setError] = useState<boolean>(false)
    const [navItems, setNavItems] = useState<(ApplyV2SettingsSection & { title: string; state: NavItemsState })[]>([])
    const numberId = useMemo(() => {
        const n = Number(id)
        return isNaN(n) ? 0 : n
    }, [id])
    const [isApplicationDone, setIsApplicationDone] = useState<boolean>(false)
    const [isReviewFinalState, setIsReviewFinalState] = useState<boolean>(false)

    const [serviceCode] = useLocalStorageStore('service_code')
    const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'open' | 'closed' | 'error'>('closed')
    const ws = useRef<WebSocket | null>(null)
    const reconnectTimeoutRef = useRef<NodeJS.Timeout>()
    const reconnectAttempts = useRef(0)
    const isConnectingRef = useRef(false)
    const lastSocketUrlRef = useRef<string | null>(null)
    const connectionAttemptRef = useRef<{ url: string; timestamp: number } | null>(null)
    const previousSocketUrlRef = useRef<string | null>(null)
    const lastProfileRefetchForCvSuccessRef = useRef<string | null>(null)
    const [isRefetchingAfterCvParsing, setIsRefetchingAfterCvParsing] = useState(false)

    const [cvParserStatus, setCvParserStatus] = useState<
        'file-search-started' | 'file-sent-to-parsing' | 'in-progress' | 'success' | null
    >(null)
    const maxReconnectAttempts = 5
    const { data: headerData } = useGetHeaderInfoQuery({}, { skip: !numberId })
    const candidateId = headerData?.data?.CandidateID ?? 0
    const key = useMemo(() => `apply_dialog_v2-${numberId}-${candidateId}` as const, [numberId, candidateId])
    const [storageData, setStorageData, removeStorageData] = useLocalStorageStore(key)
    const isTabActive = usePageVisibility()
    const { sendCommand, sendMessage, messages } = useBroadcastChannel(key)

    // console.log('CANDIDATE ID: ------>', candidateId)

    const {
        data: settingsData,
        error: settingsError,
        isLoading: isSettingsLoading,
        isFetching: isSettingsFetching,
        refetch: settingsRefetch,
        currentData,
    } = useGetApplyV2SettingsByIdQuery({ id: numberId }, { skip: !numberId || !isOpen || !storageData })

    const {
        data: postData,
        error: postError,
        // isLoading: isPostLoading,
        isFetching: isPostFetching,
        isSuccess: isPostSuccess,
        isError: isPostError,
        refetch: postRefetch,
    } = useGetPostInfoQuery(
        {
            id: numberId,
        },
        { skip: !numberId || !isOpen || !storageData }
    )

    const [
        initApplyV2,
        { data: initData, error: initError, isError: isInitError, isLoading: isInitLoading, reset: resetInitData },
    ] = useInitApplyV2Mutation()

    const {
        data: postProfileData,
        error: postProfileError,
        isFetching: isPostProfileFetching,
        isLoading: isPostProfileLoading,
        isSuccess: isPostProfileSuccess,
        refetch: postProfileRefetch,
    } = useGetProfileV2ApplyQuery(
        { uid: initData?.data?.uid },
        {
            // skip: !initData?.data?.uid || !isOpen || !!initData,
            skip: !initData?.data?.uid || !isOpen,
        }
    )

    const [putStepRequest] = usePutApplyV2StepMutation()

    const [
        postValidateAll,
        { error: validateError, isLoading: isValidateLoading, isError: isValidateError, isSuccess: isValidateSuccess },
    ] = usePostValidateAllV2ApplyMutation()

    const {
        data: questionsData,
        isError: isQuestionsError,
        isLoading: isQuestionsLoading,
        isFetching: isQuestionsFetching,
        refetch: questionsRefetch,
    } = useGetAllQuestionsV2ApplyQuery({ uid: storageData?.postInitUid || '' }, { skip: !storageData?.postInitUid })

    const [triggerSocket, { data: socketData, isLoading: isSocketLoading, isError: isSocketError }] =
        useLazyGetApplyV2SocketQuery()

    const [deleteApplyV2Profile, { isLoading: isProfileDeleteLoading }] = useDeleteApplyV2ProfileMutation()

    // const navItems = useMemo(() => {
    //     return Object.values(settingsData?.steps || {})
    //         .filter((it) => it.visible)
    //         .sort((a, b) => a.order - b.order)
    //         .map((item) => ({ ...item, title: getTitle(item.name, item.name), state: 'default' as NavItemsState }))
    // }, [settingsData])
    // console.log(navItems)

    useEffect(() => {
        if (messages.length > 0) {
            const lastMessage = messages[messages.length - 1]
            switch (lastMessage.type) {
                case 'RELOAD':
                    console.log('reload', lastMessage)
                    if (!isTabActive) {
                        window.location.reload()
                    }

                    break
                case 'NAVIGATE':
                    console.log('NAVIGATE', lastMessage)
                    window.location.href = lastMessage.path
                    break
                default:
                    return
            }
        }
    }, [messages])

    const setIsApplicationDoneHandler = (value: boolean) => {
        setIsApplicationDoneHandler(value)
    }

    const isPostApplicationExist = Boolean((initError as any)?.data?.errors?.some((error: any) => error.code === 1202))

    const onCloseApply = useCallback(async () => {
        // console.log('onCloseApply()')
        // setIsOpen(false)
        // setIsExpanded(false)
        try {
            console.log('onCloseApply()')
            setContent(null)
            dispatch(applyJobV2Api.util.resetApiState())
            removeStorageData()

            deleteApplyV2Profile({
                uid: initData?.data?.uid ?? postProfileData?.data?.uid ?? storageData?.postInitUid ?? '',
            })
                .unwrap()
                .catch(console.log)
                .finally(() => {
                    resetInitData()
                    dispatch(applyJobV2Api.util.resetApiState())
                    setIsOpen(false)
                    removeStorageData()
                })
        } catch (e) {
            console.error('onCloseApply error', e)
        }
    }, [candidateId, deleteApplyV2Profile, storageData, postProfileData, initData, resetInitData])

    useEffect(() => {
        const handleStorage = (event: StorageEvent) => {
            if (event.key === key && !!event.oldValue && !event.newValue) {
                dispatch(applyJobV2Api.util.resetApiState())

                setTimeout(() => {
                    window.location.reload()
                }, 100)
            }
        }

        window.addEventListener('storage', handleStorage)

        return () => {
            window.removeEventListener('storage', handleStorage)
        }
    }, [key])

    useEffect(() => {
        if (isPostApplicationExist) {
            onCloseApply()
            removeStorageData()
            // window.location.reload()
            sendCommand('RELOAD')
        }
    }, [isPostApplicationExist, removeStorageData, onCloseApply])

    useEffect(() => {
        if (isInitError || isPostError || !!postProfileError || isSocketError || isQuestionsError) {
            // onCloseApply()
            removeStorageData()
            dispatch(applyJobV2Api.util.resetApiState())
        }
    }, [isInitError, postProfileError, isPostError, isSocketError, isQuestionsError, removeStorageData])

    const sendWebSocketMessage = useCallback(
        (message: 'subscribe' | 'cv-parser') => {
            console.log('[ApplyV2ModalContext] sendWebSocketMessage called', {
                message,
                hasWs: !!ws.current,
                readyState: ws.current?.readyState,
                connectionStatus,
            })

            if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                try {
                    const payload = JSON.stringify({ action: message })
                    ws.current.send(payload)
                    console.log('[ApplyV2ModalContext] WebSocket message sent successfully:', {
                        message,
                        payload,
                    })
                    return true
                } catch (error) {
                    console.error('[ApplyV2ModalContext] Failed to send WebSocket message:', error)
                    return false
                }
            } else {
                console.warn('[ApplyV2ModalContext] WebSocket is not connected. Current state:', {
                    readyState: ws.current?.readyState,
                    connectionStatus,
                    hasWs: !!ws.current,
                    WebSocketStates: {
                        CONNECTING: WebSocket.CONNECTING,
                        OPEN: WebSocket.OPEN,
                        CLOSING: WebSocket.CLOSING,
                        CLOSED: WebSocket.CLOSED,
                    },
                })
                return false
            }
        },
        [connectionStatus]
    )

    const disconnectWebSocket = useCallback(() => {
        console.log('[ApplyV2ModalContext] disconnectWebSocket called', {
            hasWs: !!ws.current,
            readyState: ws.current?.readyState,
            currentStatus: connectionStatus,
            isConnecting: isConnectingRef.current,
        })

        isConnectingRef.current = false

        if (reconnectTimeoutRef.current) {
            console.log('[ApplyV2ModalContext] Clearing reconnect timeout')
            clearTimeout(reconnectTimeoutRef.current)
            reconnectTimeoutRef.current = undefined
        }

        if (ws.current) {
            const readyState = ws.current.readyState
            const url = ws.current.url
            console.log('[ApplyV2ModalContext] Closing WebSocket connection', {
                readyState,
                url,
            })

            ws.current.onopen = null
            ws.current.onmessage = null
            ws.current.onclose = null
            ws.current.onerror = null

            if (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING) {
                console.log('[ApplyV2ModalContext] WebSocket is open or connecting, closing...')
                ws.current.close()
            } else {
                console.log('[ApplyV2ModalContext] WebSocket is not open, readyState:', readyState)
            }
            ws.current = null
            console.log('[ApplyV2ModalContext] WebSocket instance cleared')
        } else {
            console.log('[ApplyV2ModalContext] No WebSocket instance to disconnect')
        }
        setConnectionStatus('closed')
        console.log('[ApplyV2ModalContext] Connection status set to closed')
    }, [])

    const connectWebSocket = useCallback(
        (url: string) => {
            const now = Date.now()
            if (ws.current && ws.current.readyState === WebSocket.OPEN && lastSocketUrlRef.current === url) {
                return
            }

            if (ws.current && ws.current.readyState === WebSocket.CONNECTING && lastSocketUrlRef.current === url) {
                return
            }
            if (
                connectionAttemptRef.current &&
                connectionAttemptRef.current.url === url &&
                now - connectionAttemptRef.current.timestamp < 2000
            ) {
                return
            }

            if (isConnectingRef.current && lastSocketUrlRef.current === url) {
                return
            }

            console.log('[ApplyV2ModalContext] connectWebSocket called', {
                url,
                currentStatus: connectionStatus,
                existingWs: !!ws.current,
                existingWsState: ws.current?.readyState,
                isConnecting: isConnectingRef.current,
                lastSocketUrl: lastSocketUrlRef.current,
                lastAttempt: connectionAttemptRef.current,
            })

            isConnectingRef.current = true
            lastSocketUrlRef.current = url
            connectionAttemptRef.current = { url, timestamp: now }

            disconnectWebSocket()

            try {
                console.log('[ApplyV2ModalContext] Creating new WebSocket connection:', url)
                setConnectionStatus('connecting')

                ws.current = new window.WebSocket(url)
                console.log('[ApplyV2ModalContext] WebSocket instance created', {
                    readyState: ws.current.readyState,
                    url: ws.current.url,
                })

                ws.current.onopen = (event) => {
                    console.log('[ApplyV2ModalContext] WebSocket connected successfully', {
                        readyState: ws.current?.readyState,
                        url: ws.current?.url,
                    })
                    isConnectingRef.current = false
                    setConnectionStatus('open')
                    reconnectAttempts.current = 0

                    // if (ws.current?.readyState === WebSocket.OPEN) {
                    //     ws.current.send(JSON.stringify({ action: 'subscribe' }))
                    //     ws.current.send(JSON.stringify({ action: 'cv-parser' }))
                    // }
                }

                ws.current.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data)
                        console.log('[ApplyV2ModalContext] WebSocket message received:', data)
                        // const res = { action: 'subscribe', status: 'success' }
                        // const pars = { action: 'cv-parser', status: 'file-sent-to-parsing' | 'in-progress' | 'success' }

                        if (data.action === 'cv-parser') {
                            console.log('[ApplyV2ModalContext] CV parser status update:', data.status)
                            switch (data.status as CvParsingStatus) {
                                case 'file-search-started':
                                    console.log('[ApplyV2ModalContext] Setting cvParserStatus to file-search-started')
                                    setCvParserStatus(data.status)
                                    return
                                case 'file-sent-to-parsing':
                                    console.log('[ApplyV2ModalContext] Setting cvParserStatus to file-sent-to-parsing')
                                    setCvParserStatus(data.status)
                                    return
                                case 'in-progress':
                                    console.log('[ApplyV2ModalContext] Setting cvParserStatus to in-progress')
                                    setCvParserStatus(data.status)
                                    return
                                case 'success':
                                    console.log(
                                        '[ApplyV2ModalContext] CV parser SUCCESS! Setting cvParserStatus to success'
                                    )
                                    const successUid = postProfileData?.data?.uid ?? initData?.data?.uid
                                    const successCacheKey = successUid ? `${successUid}-success` : null

                                    if (
                                        successCacheKey &&
                                        lastProfileRefetchForCvSuccessRef.current === successCacheKey
                                    ) {
                                        console.log(
                                            '[ApplyV2ModalContext] Already refetched profile for this CV success, skipping...',
                                            {
                                                cacheKey: successCacheKey,
                                            }
                                        )
                                    } else if (successCacheKey) {
                                        console.log('[ApplyV2ModalContext] Marking CV success for profile refetch', {
                                            cacheKey: successCacheKey,
                                        })
                                        lastProfileRefetchForCvSuccessRef.current = successCacheKey
                                    }

                                    setCvParserStatus(data.status)
                                    // postProfileRefetch && postProfileRefetch() // TODO - Disabled to prevent updating parsed data
                                    return
                                default:
                                    console.log('[ApplyV2ModalContext] Unknown CV parser status:', data.status)
                                    return
                            }
                        } else if (data.action === 'cv-parser-status') {
                            console.log('[ApplyV2ModalContext] CV parser status (alternative):', data.status)
                            switch (data.status as CvParsingStatus) {
                                case 'in-progress':
                                    console.log(
                                        '[ApplyV2ModalContext] Setting cvParserStatus to in-progress (from cv-parser-status)'
                                    )
                                    setCvParserStatus(data.status)
                                    return
                                case 'error':
                                    console.log(
                                        '[ApplyV2ModalContext] CV parser ERROR! Setting cvParserStatus to error'
                                    )
                                    setCvParserStatus(data.status)
                                    return
                                case 'success':
                                    console.log(
                                        '[ApplyV2ModalContext] CV parser SUCCESS (from cv-parser-status)! Setting cvParserStatus to success'
                                    )
                                    const successUid2 = postProfileData?.data?.uid ?? initData?.data?.uid
                                    const successCacheKey2 = successUid2 ? `${successUid2}-success` : null

                                    if (
                                        successCacheKey2 &&
                                        lastProfileRefetchForCvSuccessRef.current === successCacheKey2
                                    ) {
                                        console.log(
                                            '[ApplyV2ModalContext] Already refetched profile for this CV success (from cv-parser-status), skipping...',
                                            {
                                                cacheKey: successCacheKey2,
                                            }
                                        )
                                    } else if (successCacheKey2) {
                                        console.log(
                                            '[ApplyV2ModalContext] Marking CV success for profile refetch (from cv-parser-status)',
                                            {
                                                cacheKey: successCacheKey2,
                                            }
                                        )
                                        lastProfileRefetchForCvSuccessRef.current = successCacheKey2
                                    }

                                    setCvParserStatus(data.status)
                                    // postProfileRefetch && postProfileRefetch()  // TODO - Disabled to prevent updating parsed data
                                    return
                                default:
                                    console.log(
                                        '[ApplyV2ModalContext] Unknown CV parser status (from cv-parser-status):',
                                        data.status
                                    )
                                    return
                            }
                        } else {
                            console.log('[ApplyV2ModalContext] Unknown WebSocket message action:', data.action)
                        }
                    } catch (error) {
                        console.log('[ApplyV2ModalContext] Raw WebSocket message (parse error):', {
                            data: event.data,
                            error,
                        })
                    }
                }

                ws.current.onclose = (event) => {
                    console.log('[ApplyV2ModalContext] WebSocket connection closed:', {
                        code: event.code,
                        reason: event.reason,
                        wasClean: event.wasClean,
                    })
                    isConnectingRef.current = false
                    setConnectionStatus('closed')

                    if (
                        event.code !== 1008 &&
                        reconnectAttempts.current < maxReconnectAttempts &&
                        storageData?.socketUrl
                    ) {
                        reconnectAttempts.current += 1
                        const delay = 3000 * reconnectAttempts.current

                        console.log(
                            `[ApplyV2ModalContext] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})...`
                        )

                        reconnectTimeoutRef.current = setTimeout(() => {
                            const socketUrl = storageData?.socketUrl
                            const shouldReconnect =
                                socketUrl &&
                                (!ws.current || ws.current.readyState === WebSocket.CLOSED) &&
                                !isConnectingRef.current

                            if (shouldReconnect && socketUrl) {
                                console.log('[ApplyV2ModalContext] Attempting to reconnect...', {
                                    socketUrl,
                                })
                                connectWebSocket(socketUrl)
                            } else {
                                console.log(
                                    '[ApplyV2ModalContext] Skipping reconnect - already connected or connecting',
                                    {
                                        hasSocketUrl: !!socketUrl,
                                        wsReadyState: ws.current?.readyState,
                                        isConnecting: isConnectingRef.current,
                                    }
                                )
                            }
                        }, delay)
                    } else {
                        console.log('[ApplyV2ModalContext] Not reconnecting', {
                            code: event.code,
                            maxAttemptsReached: reconnectAttempts.current >= maxReconnectAttempts,
                            attempts: reconnectAttempts.current,
                            hasSocketUrl: !!storageData?.socketUrl,
                        })
                    }
                }

                ws.current.onerror = (error) => {
                    console.error('[ApplyV2ModalContext] WebSocket error:', {
                        error,
                        readyState: ws.current?.readyState,
                        url: ws.current?.url,
                    })
                    isConnectingRef.current = false
                    setConnectionStatus('error')
                }
            } catch (error) {
                console.error('[ApplyV2ModalContext] WebSocket connection failed:', error)
                isConnectingRef.current = false
                setConnectionStatus('error')
            }
        },
        [disconnectWebSocket, storageData?.socketUrl, maxReconnectAttempts, connectionStatus]
    )

    const reconnectWebSocket = useCallback(() => {
        if (storageData?.socketUrl) {
            reconnectAttempts.current = 0
            connectWebSocket(storageData.socketUrl)
        }
    }, [storageData?.socketUrl, connectWebSocket])

    const initWebSocketConnection = useCallback(async () => {
        console.log('[ApplyV2ModalContext] initWebSocketConnection called', {
            postInitUid: storageData?.postInitUid,
            initDataUid: initData?.data?.uid,
            postProfileDataUid: postProfileData?.data?.uid,
            currentSocketUrl: storageData?.socketUrl,
            connectionStatus,
            isOpen,
            isConnecting: isConnectingRef.current,
        })

        if (storageData?.socketUrl) {
            const isAlreadyConnected =
                ws.current &&
                ws.current.readyState === WebSocket.OPEN &&
                lastSocketUrlRef.current === storageData.socketUrl

            if (isAlreadyConnected) {
                console.log('[ApplyV2ModalContext] WebSocket already connected, skipping init', {
                    socketUrl: storageData.socketUrl,
                    connectionStatus,
                })
                return
            }
        }

        const uid = storageData?.postInitUid ?? initData?.data?.uid ?? postProfileData?.data?.uid

        if (!uid) {
            console.warn('[ApplyV2ModalContext] Cannot init WebSocket: UID is not available', {
                postInitUid: storageData?.postInitUid,
                initDataUid: initData?.data?.uid,
                postProfileDataUid: postProfileData?.data?.uid,
            })
            return
        }

        try {
            console.log('[ApplyV2ModalContext] Fetching WebSocket URL from API...', { uid })
            const result = await triggerSocket({ uid })
            console.log('[ApplyV2ModalContext] Socket API response:', {
                hasData: !!result.data,
                hasAuthUrl: !!result.data?.data?.authUrl,
                fullResponse: result,
            })

            const authUrl = result.data?.data?.authUrl

            if (authUrl && storageData) {
                if (storageData.socketUrl === authUrl) {
                    console.log('[ApplyV2ModalContext] Socket URL already set, skipping update:', authUrl)
                    return
                }

                console.log('[ApplyV2ModalContext] Setting WebSocket URL in storageData:', authUrl)
                setStorageData({ ...storageData, socketUrl: authUrl })
                console.log(
                    '[ApplyV2ModalContext] StorageData updated with socketUrl. Connection will be triggered by useEffect.'
                )
            } else if (authUrl && !storageData) {
                console.warn('[ApplyV2ModalContext] Cannot set socketUrl: storageData is null', { authUrl })
            } else {
                console.error('[ApplyV2ModalContext] Failed to get WebSocket URL from API', {
                    hasAuthUrl: !!authUrl,
                    hasStorageData: !!storageData,
                    resultData: result.data,
                })
            }
        } catch (error) {
            console.error('[ApplyV2ModalContext] Failed to initialize WebSocket connection:', error)
        }
    }, [
        storageData,
        initData?.data?.uid,
        postProfileData?.data?.uid,
        triggerSocket,
        connectionStatus,
        setStorageData,
        isOpen,
    ])

    useEffect(() => {
        const socketUrl = storageData?.socketUrl

        if (socketUrl === previousSocketUrlRef.current && !isOpen) {
            if (ws.current) {
                console.log('[ApplyV2ModalContext] Disconnecting WebSocket - modal closed', {
                    socketUrl,
                })
                disconnectWebSocket()
            }
            return
        }

        if (!socketUrl || !isOpen) {
            if (ws.current) {
                console.log('[ApplyV2ModalContext] Disconnecting WebSocket - no URL or modal closed', {
                    hasSocketUrl: !!socketUrl,
                    isOpen,
                })
                disconnectWebSocket()
            }
            previousSocketUrlRef.current = socketUrl || null
            return
        }

        if (socketUrl === previousSocketUrlRef.current) {
            const wsReadyState = ws.current?.readyState
            const isAlreadyConnected = wsReadyState === WebSocket.OPEN && lastSocketUrlRef.current === socketUrl
            if (isAlreadyConnected) {
                console.log('[ApplyV2ModalContext] URL unchanged and already connected, skipping', {
                    socketUrl,
                    wsReadyState,
                })
                return
            }
        }

        previousSocketUrlRef.current = socketUrl

        const wsReadyState = ws.current?.readyState
        const isAlreadyConnected = wsReadyState === WebSocket.OPEN && lastSocketUrlRef.current === socketUrl
        const isAlreadyConnecting =
            (wsReadyState === WebSocket.CONNECTING || isConnectingRef.current) && lastSocketUrlRef.current === socketUrl

        const now = Date.now()
        const recentAttempt =
            connectionAttemptRef.current &&
            connectionAttemptRef.current.url === socketUrl &&
            now - connectionAttemptRef.current.timestamp < 2000

        console.log('[ApplyV2ModalContext] useEffect for socketUrl triggered', {
            socketUrl,
            previousSocketUrl: previousSocketUrlRef.current,
            isOpen,
            wsReadyState,
            isAlreadyConnected,
            isAlreadyConnecting,
            isConnecting: isConnectingRef.current,
            lastSocketUrl: lastSocketUrlRef.current,
            recentAttempt,
            lastAttemptTime: connectionAttemptRef.current?.timestamp,
        })

        if (!isAlreadyConnected && !isAlreadyConnecting && !recentAttempt) {
            console.log('[ApplyV2ModalContext] All checks passed, connecting to WebSocket...', {
                url: socketUrl,
            })
            connectWebSocket(socketUrl)
        } else {
            console.log('[ApplyV2ModalContext] Skipping connection - checks failed', {
                isAlreadyConnected,
                isAlreadyConnecting,
                recentAttempt,
                url: socketUrl,
            })
        }
    }, [storageData?.socketUrl, isOpen, connectWebSocket, disconnectWebSocket])

    // useEffect(() => {
    //     console.log('WebSocket state:', {
    //         readyState: ws.current?.readyState,
    //         connectionStatus,
    //         url: ws.current?.url,
    //         reconnectAttempts: reconnectAttempts.current,
    //     })
    // }, [connectionStatus])

    // console.log(
    //     'WS: ,',
    //     ws,
    //     ws.current?.readyState,
    //     WebSocket.CONNECTING,
    //     WebSocket.OPEN,
    //     WebSocket.CLOSING,
    //     WebSocket.CLOSED,
    //     connectionStatus
    // )

    useEffect(() => {
        if (initData?.data?.uid && isOpen && connectionStatus === 'closed') {
            const fetchSocketUrl = async () => {
                try {
                    console.log('Fetch Socket:', storageData?.postInitUid, postProfileData?.data?.uid)
                    const result = await triggerSocket({
                        uid: initData?.data?.uid ?? postProfileData?.data?.uid ?? storageData?.postInitUid ?? '',
                    })
                    console.log('SOCKET res', result)
                    const authUrl = result.data?.data?.authUrl

                    if (authUrl && storageData) {
                        console.log('Setting WebSocket URL:', authUrl)
                        setStorageData({ ...storageData, socketUrl: authUrl })
                    }
                } catch (error) {
                    console.error('Failed to get WebSocket URL:', error)
                }
            }

            fetchSocketUrl().catch((e) => {
                console.error('Failed to fetch WebSocket URL:', e)
            })
        }
    }, [initData?.data?.uid, postProfileData?.data?.uid, storageData, setStorageData, isOpen, triggerSocket])

    useEffect(() => {
        if (postProfileData?.data?.uid) {
            postValidateAll({
                uid: postProfileData?.data?.uid,
            })
        }
    }, [postProfileData?.data?.uid])

    const setNavItemsStates = (items: { name: ApplyV2SettingsSection['name']; state: NavItemsState }[]) => {
        setNavItems((prev) => {
            return prev.map((nav) => ({ ...nav, state: items.find((f) => f.name === nav.name)?.state ?? nav.state }))
        })
    }

    useEffect(() => {
        setNavItems(
            Object.values(settingsData?.steps || {})
                .filter((it) => it.visible)
                .sort((a, b) => a.order - b.order)
                .map((item) => {
                    if (item.name === 'driverLicence') {
                        return { ...item, title: getTitle('license', 'License'), state: 'default' as NavItemsState }
                    }
                    return { ...item, title: getTitle(item.name, item.name), state: 'default' as NavItemsState }
                })
        )
    }, [settingsData])

    useEffect(() => {
        // TODO - added this for defered validate nav state
        if (isPostProfileLoading || isValidateLoading || isInitLoading) {
            // setNavItemsStates(
            //     navItems.map((i) => ({
            //         ...i,
            //         state: 'optional',
            //     }))
            // )
        } else if (validateError && 'data' in validateError) {
            const dataError = (validateError as FetchBaseQueryError & { data: ApplyV2BaseResponse }).data
            const errs = (Array.isArray(dataError?.errors) ? dataError.errors : [])
                .map((e: any) => e.data)
                .filter(Boolean)
                .flat()

            const hasProfileErrors = (Array.isArray(dataError?.errors) ? dataError.errors : []).some(
                (e: any) => e.profileErrors && Array.isArray(e.profileErrors) && e.profileErrors.length > 0
            )

            const errorsList = hasProfileErrors && !errs.includes('profile') ? [...errs, 'profile'] : errs
            setNavItemsStates(
                navItems.map((i) => {
                    const sectionData = postProfileData?.data && (postProfileData.data as Record<string, any>)[i.name]
                    const noFields = postProfileData?.data?.noFields ?? []
                    let isNotEmpty = !!sectionData
                    let isDriverLicenceNoField = false
                    if (i.name === 'driverLicence' && !!sectionData && Array.isArray(sectionData)) {
                        isNotEmpty = sectionData.length > 0 && !sectionData.includes(66)
                        isDriverLicenceNoField = !!sectionData && Array.isArray(sectionData) && sectionData.includes(66)
                        return {
                            ...i,
                            state: isNotEmpty || isDriverLicenceNoField ? 'success' : i.state,
                        }
                    }
                    if (i.name === 'question') {
                        isNotEmpty = (questionsData?.data?.questions ?? []).length > 0
                    }
                    if (i.name === 'skill' || i.name === 'language') {
                        return {
                            ...i,
                            // state: 'success',
                            state: isNotEmpty || noFields.includes(i.name) ? 'success' : 'optional',
                        }
                    }
                    return {
                        ...i,
                        // state: errorsList.includes(i.name) ? 'error' : isNotEmpty ? 'success' : i.state,
                        state: errorsList.includes(i.name)
                            ? 'error'
                            : isNotEmpty || noFields.includes(i.name)
                              ? 'success'
                              : // : i.state, // TODO - add optional state -try
                                'optional',
                    }
                })
            )
        } else if (isValidateSuccess) {
            setNavItemsStates(
                navItems.map((i) => {
                    const sectionData = postProfileData?.data && (postProfileData.data as Record<string, any>)[i.name]
                    const noFields = postProfileData?.data?.noFields ?? []
                    let isNotEmpty = !!sectionData
                    let isDriverLicenceNoField = false
                    if (i.name === 'driverLicence' && !!sectionData && Array.isArray(sectionData)) {
                        isNotEmpty = sectionData.length > 0 && !sectionData.includes(66)
                        isDriverLicenceNoField = !!sectionData && Array.isArray(sectionData) && sectionData.includes(66)
                        return {
                            ...i,
                            state: isNotEmpty || isDriverLicenceNoField ? 'success' : i.state,
                        }
                    }
                    if (i.name === 'question') {
                        isNotEmpty = (questionsData?.data?.questions ?? []).length > 0
                    }
                    if (i.name === 'skill' || i.name === 'language') {
                        return {
                            ...i,
                            // state: 'success',
                            state: isNotEmpty || noFields.includes(i.name) ? 'success' : 'optional',
                        }
                    }
                    return {
                        ...i,
                        // state: 'success',
                        state: isNotEmpty || noFields.includes(i.name) ? 'success' : 'optional',
                        // state: isNotEmpty || noFields.includes(i.name) ? 'success' : i.state,
                    }
                })
            )
        }
        // }, [validateError, isValidateSuccess, navItems, setNavItems, questionsData?.data?.questions])
    }, [validateError, isValidateSuccess])

    useEffect(() => {
        if (postData?.data?.ad?.AdID && storageData) {
            const title = decodeHtmlEntitiesRecursive(postData?.data?.ad?.JobTitle ?? '')
            // console.log('useEffect 1 - OK', storageData.id, postData.data.ad.AdID)
            if (storageData.id === postData.data.ad.AdID) {
                if (storageData.title !== title) {
                    setStorageData({
                        ...storageData,
                        title: title,
                        expiredDate: postData?.data?.ad?.Expire ?? '',
                    })
                }
            }
        }
    }, [postData, storageData, setStorageData])

    useEffect(() => {
        const statuses = ['file-search-started', 'file-sent-to-parsing', 'in-progress'] as Array<CvParsingStatus>
        if (cvParserStatus && statuses.includes(cvParserStatus)) {
            setNavItems((prev) => {
                return prev.map((nav) => {
                    if (nav.name === 'cv') {
                        return {
                            ...nav,
                            state: 'parsing',
                        }
                    }
                    return { ...nav }
                })
            })
        } else if (cvParserStatus === 'success') {
            setNavItems((prev) => {
                return prev.map((nav) => {
                    if (nav.name === 'cv') {
                        return {
                            ...nav,
                            state: 'success',
                        }
                    }
                    return { ...nav }
                })
            })
        } else {
            // setNavItems((prev) => {
            //   return prev.map((nav) => {
            //     if (nav.name === 'cv') {
            //       return {
            //         ...nav,
            //         state: 'optional',
            //       }
            //     }
            //     return { ...nav }
            //   })
            // })
        }
    }, [cvParserStatus, setNavItems])

    useEffect(() => {
        console.log(
            '%cSTEP STORAGE SET:',
            'color: #4CAF50; font-weight: bold; font-size: 14px;',
            storageData?.currentStep,
            initData?.data?.step,
            // postProfileData?.data?.step,
            navItems[0]?.name
        )
        if (initData?.data?.uid && storageData && storageData?.postInitUid !== initData?.data?.uid) {
            const allSteps = [
                ...(storageData.existingSteps || []),
                storageData.currentStep,
                // initData.data.step,
                // postProfileData?.data?.step,
                navItems[0]?.name,
            ].filter((step): step is ApplyV2SettingsSectionName => step != null)

            const uniqueSteps = Array.from(new Set(allSteps))

            setStorageData({
                ...storageData,
                postInitUid: initData.data.uid,
                currentStep: storageData.currentStep ?? initData.data.step ?? navItems[0]?.name ?? null,
                existingSteps: uniqueSteps,
            })
        }
    }, [initData, storageData, setStorageData, postProfileData, navItems, isOpen])

    useEffect(() => {
        setIsExpanded(storageData?.expanded ?? false)
        setIsOpen(storageData?.opened ?? false)

        const handleVisibilityChange = () => {
            if (storageData?.id && storageData?.opened && storageData?.expanded && initApplyV2 && !initData) {
                if (!document.hidden) {
                    try {
                        initApplyV2({ id: numberId })
                        // console.log('initApplyV2() id: ', numberId)
                    } catch (e) {
                        console.log('initApplyV2() error useEffect: ', e)
                    }
                }
            }
        }

        handleVisibilityChange()

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [storageData?.id, storageData?.expanded, storageData?.opened, numberId, initApplyV2, initData])

    const setIsFinalStep = useCallback(
        (value: boolean) => {
            console.log('setIsFinalStep', value)
            if (!storageData) {
                console.warn('[ApplyV2ModalContext] Cannot set isFinalStep: storageData is null.')
                return
            }
            setStorageData({ ...storageData, isFinalStep: value })
        },
        [storageData, setStorageData]
    )

    const onResizeApply = useCallback(() => {
        console.log('resize')
        if (!storageData) {
            console.warn('[ApplyV2ModalContext] Cannot resize modal: storageData is null.')
            return
        }

        if (!storageData.expanded) {
            postProfileRefetchHandler().catch(console.log)
        }
        setStorageData({ ...storageData, expanded: !storageData.expanded })
    }, [storageData, setStorageData])

    // const onOpenApply = useCallback(
    //     (component?: ReactNode) => {
    //         setContent(component ?? null)
    //         setIsOpen(true)
    //         setStorageData({
    //             id: Number(id ?? storageData?.id ?? 0),
    //             opened: true,
    //             expanded: true,
    //             title: decodeHtmlEntitiesRecursive(storageData?.title ?? postData?.data?.ad?.JobTitle ?? ''),
    //             expiredDate: storageData?.expiredDate || postData?.data?.ad?.Expire || '',
    //             link: storageData?.link || settingsData?.externalLink,
    //         })
    //     },
    //     [storageData, postData?.data?.ad?.JobTitle, postData?.data?.ad?.Expire, settingsData?.externalLink, id]
    // )

    const onNextStep = useCallback(() => {
        if (!storageData) {
            console.warn('[ApplyV2ModalContext] Cannot go to next step: storageData is null.')
            return
        }
        if (!storageData?.postInitUid) {
            console.warn('[ApplyV2ModalContext] Cannot go to next step: postInitUid is null.')
            return
        }
        let currentIndex = navItems.findIndex((item) => item.name === storageData.currentStep)
        if (currentIndex < 0 && navItems.length > 0) {
            currentIndex = 0
        }
        if (currentIndex < 0 || currentIndex >= navItems.length - 1) {
            console.log(navItems)
            console.warn('[ApplyV2ModalContext] No next step available.')
            return
        }
        const currentStep = storageData.currentStep ?? navItems[currentIndex].name
        const updatedExistingStepsSet = new Set(storageData.existingSteps || [])
        const nextStep = navItems[currentIndex + 1]?.name
        // if (currentStep && !updatedExistingSteps.includes(currentStep)) {
        //     updatedExistingSteps.push(currentStep)
        // }
        if (currentStep) {
            updatedExistingStepsSet.add(currentStep)
            updatedExistingStepsSet.add(nextStep)
        }
        // console.log(updatedExistingStepsSet)
        setStorageData({ ...storageData, currentStep: nextStep, existingSteps: [...updatedExistingStepsSet] })
        try {
            putStepRequest({ uid: storageData.postInitUid, step: nextStep })
            postValidateAll({ uid: storageData.postInitUid })
            postProfileRefetchHandler()
        } catch (e) {
            console.log('onNextStep error', e)
        }
    }, [storageData, navItems, putStepRequest, postProfileData, postProfileRefetch, initApplyV2])

    const onOpenApply = (component?: ReactNode) => {
        const title = decodeHtmlEntitiesRecursive(postData?.data?.ad?.JobTitle)
        console.log('onOpenApply()', title, storageData?.title)
        const existingStepsSet = new Set<ApplyV2SettingsSectionName>()

        if (storageData?.existingSteps && Array.isArray(storageData.existingSteps)) {
            storageData.existingSteps.forEach((step) => {
                if (step) existingStepsSet.add(step)
            })
        }

        if (existingStepsSet.size === 0) {
            const potentialSteps = [
                storageData?.currentStep,
                // initData?.data?.step,
                // postProfileData?.data?.step,
                navItems[0]?.name,
            ].filter((step): step is ApplyV2SettingsSectionName => step != null)

            potentialSteps.forEach((step) => existingStepsSet.add(step))
        }

        setContent(component ?? null)
        setStorageData({
            id: numberId,
            opened: true,
            expanded: true,
            title: title,
            expiredDate: postData?.data?.ad?.Expire ?? '',
            link: settingsData?.externalLink,
            currentStep:
                storageData?.currentStep ??
                // initData?.data?.step ??
                // postProfileData?.data?.step ??
                navItems[0]?.name ??
                null,
            postInitUid: initData?.data?.uid ?? null,
            isFinalStep: storageData?.isFinalStep ?? false,
            isPostApplied: storageData?.isPostApplied ?? false,
            existingSteps: Array.from(existingStepsSet),
            socketUrl: storageData?.socketUrl,
            anonymityCheckbox: false,
        })
    }

    const onSelectStep = useCallback(
        async (step: ApplyV2SettingsSectionName | null) => {
            if (!storageData) {
                console.warn('[ApplyV2ModalContext] Cannot select step: storageData is null.')
                return
            }

            const currentStep = storageData.currentStep
            const currentStepIndex = navItems.findIndex((item) => item.name === currentStep)
            const targetStepIndex = step ? navItems.findIndex((item) => item.name === step) : -1

            if (currentStep === 'profile' && step && targetStepIndex > currentStepIndex && storageData?.postInitUid) {
                try {
                    const validationResult = await postValidateAll({ uid: storageData.postInitUid }).unwrap()

                    if (validationResult && 'errors' in validationResult && Array.isArray(validationResult.errors)) {
                        const hasProfileErrors = validationResult.errors.some(
                            (e: any) => e.profileErrors && Array.isArray(e.profileErrors) && e.profileErrors.length > 0
                        )

                        if (hasProfileErrors) {
                            console.warn(
                                '[ApplyV2ModalContext] Cannot proceed to next step: profile validation errors found'
                            )
                            return
                        }
                    }
                } catch (error: any) {
                    const errorData = error?.data
                    if (errorData && 'errors' in errorData && Array.isArray(errorData.errors)) {
                        const hasProfileErrors = errorData.errors.some(
                            (e: any) => e.profileErrors && Array.isArray(e.profileErrors) && e.profileErrors.length > 0
                        )

                        if (hasProfileErrors) {
                            console.warn(
                                '[ApplyV2ModalContext] Cannot proceed to next step: profile validation errors found'
                            )
                            return
                        }
                    }
                    console.error('[ApplyV2ModalContext] Error validating profile before step change:', error)
                }
            }

            const updatedExistingStepsSet = new Set(storageData.existingSteps || [])
            if (step) {
                updatedExistingStepsSet.add(step)
            }
            setStorageData({
                ...storageData,
                currentStep: step,
                isFinalStep: false,
                existingSteps: [...updatedExistingStepsSet],
            })
            if (step && storageData?.postInitUid) {
                putStepRequest({ uid: storageData.postInitUid, step: step })
            }
            postValidateAll({ uid: storageData.postInitUid })
            postProfileRefetchHandler().catch(console.error)
        },
        [storageData, setStorageData, navItems, postValidateAll]
    )

    // console.log('ApplyV2ModalContext render')

    // useEffect(() => {
    //     if (dayjs(storageData?.job?.expiredDate).diff(dayjs(), 'day') < 0) {
    //         // closeModal()
    //         console.warn(`[ApplyV2ModalContext closeModal()] Modal expired: ${storageData?.job?.expiredDate}`)
    //     }
    // }, [storageData])

    const postProfileRefetchHandler = async () => {
        console.log('[ApplyV2ModalContext] PostProfileRefetchHandler()')
        try {
            if (postProfileData && postProfileRefetch) {
                const result = await postProfileRefetch()
                return result?.data ?? null
            } else {
                return initApplyV2({ id: numberId }).unwrap()
            }
        } catch (error) {
            console.error('Error during refetch:', error)
            return null
        }
    }

    const questionsDataRefetchHandler = async () => {
        try {
            if (questionsRefetch) {
                const result = await questionsRefetch().unwrap()
                return result?.data?.questions ?? []
            }
        } catch (e) {
            console.error('Error during refetch:', error)
            return []
        }
    }

    const handlePostValidateAll = useCallback(async () => {
        const uid = storageData?.postInitUid ?? postProfileData?.data?.uid ?? initData?.data?.uid

        if (!uid) {
            console.warn('[ApplyV2ModalContext] Cannot validate: uid is not available')
            return Promise.reject(new Error('UID is not available'))
        }

        return postValidateAll({ uid }).unwrap()
    }, [storageData?.postInitUid, postProfileData?.data?.uid, initData?.data?.uid, postValidateAll])

    const incrementProfileVisitCount = useCallback(() => {
        if (!storageData) {
            console.warn('[ApplyV2ModalContext] Cannot increment profile visit count: storageData is null.')
            return
        }
        const currentCount = storageData.profileVisitCount || 0
        const newCount = currentCount + 1
        console.log('[ApplyV2ModalContext] Incrementing profile visit count:', { currentCount, newCount })
        setStorageData({ ...storageData, profileVisitCount: newCount })
    }, [storageData, setStorageData])

    return (
        <ApplyV2ModalContext.Provider
            value={{
                isOpen,
                isExpanded,
                onOpenApply,
                onCloseApply,
                onResizeApply,
                content,
                storageData,
                error,
                settingsError,
                settingsRefetch,
                settingsData,
                isSettingsLoading,
                // isPostLoading,
                postRefetch,
                isPostFetching,
                isSettingsFetching,
                postData: postData?.data ?? null,
                onSelectStep,
                initData,
                postProfileData: postProfileData ?? initData,
                postProfileError: postProfileError ?? initError,
                isPostProfileFetching,
                isPostProfileLoading: (() => {
                    const shouldShowLoader = (isInitLoading || isPostProfileFetching) && !isRefetchingAfterCvParsing
                    return shouldShowLoader
                })(),
                isPostProfileSuccess: isPostProfileSuccess,
                isProfileDataLoading: isInitLoading || isPostProfileFetching || isPostProfileLoading,
                postProfileRefetch: postProfileRefetchHandler,
                isRefetchingAfterCvParsing,
                setIsRefetchingAfterCvParsing,
                initError,
                isInitError,
                isInitLoading,
                onNextStep,
                navItems,
                existingSteps: storageData?.existingSteps ?? [],
                setNavItemsStates,
                isFinalStep: storageData?.isFinalStep ?? false,
                setIsFinalStep,
                isPostApplied: storageData?.isPostApplied ?? false,
                isValidateLoading,
                isValidateError,
                postValidateAll: handlePostValidateAll,
                isPostApplicationExist,
                sendWebSocketMessage,
                isWebSocketConnected: connectionStatus === 'open',
                webSocketConnectionStatus: connectionStatus,
                reconnectWebSocket,
                initWebSocketConnection,
                cvParserStatus,
                isApplicationDone,
                setIsApplicationDoneHandler,
                candidateId,
                setAnonymityCheckbox: (val) => {
                    if (storageData) {
                        setStorageData({
                            ...storageData,
                            anonymityCheckbox: val,
                        })
                    }
                },
                questionsData: questionsData?.data?.questions ?? [],
                isQuestionsFetching,
                isQuestionsLoading,
                questionsRefetch: questionsDataRefetchHandler,
                incrementProfileVisitCount,
                applyKey: key,
                isReviewFinalState,
                setIsReviewFinalState,
            }}
        >
            {children}
            {isOpen && <ApplyDialogV2 />}
        </ApplyV2ModalContext.Provider>
    )
}

export const useApplyV2Context = () => useContext(ApplyV2ModalContext)
